import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';
import { User } from '@/models/user.ts';
import { PaginatedResponse } from '@/api/types.ts';

export interface CreateUserRequest {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    role_id: string;
}

export interface UpdateUserRequest
    extends Omit<CreateUserRequest, 'password' | 'password_confirmation'> {
    id: string;
}

export interface ResetPasswordRequest {
    id: string;
    password: string;
}

export const useUsers = (filters: Record<string, any> = {}) => {
    return useQuery<PaginatedResponse<User>>({
        queryKey: ['users', filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params.append(key, value.toString());
                }
            });
            const { data } = await httpClient.get<PaginatedResponse<User>>(
                `${API_ENDPOINTS.CRM.USERS}?${params}`
            );
            return data;
        },
    });
};

export const useGetUserById = (userId: string) => {
    return useQuery<User>({
        queryKey: ['users', userId],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<User>>(
                `${API_ENDPOINTS.CRM.USERS}/${userId}`
            );
            return data.data;
        },
        enabled: !!userId,
    });
};

export const useCreateUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (userData: CreateUserRequest) => {
            const { data } = await httpClient.post<ApiResponse<User>>(
                API_ENDPOINTS.CRM.USERS,
                userData
            );
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
};

export const useUpdateUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (userData: UpdateUserRequest) => {
            const { data } = await httpClient.put<ApiResponse<User>>(
                `${API_ENDPOINTS.CRM.USERS}/${userData.id}`,
                userData
            );
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
};

export const useUpdateUserField = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            userId,
            field,
            value,
        }: {
            userId: string;
            field: string;
            value: any;
        }) => {
            const { data } = await httpClient.put<ApiResponse<User>>(
                `${API_ENDPOINTS.CRM.USERS}/${userId}`,
                { [field]: value }
            );
            return data.data;
        },
        onSuccess: updatedUser => {
            // Invalidate and update specific user queries
            queryClient.invalidateQueries({ queryKey: ['users'] });
            queryClient.invalidateQueries({ queryKey: ['users', updatedUser.id] });
            // Invalidate profile so click-to-call sees updated extension when user edits self
            queryClient.invalidateQueries({ queryKey: ['user-profile'] });

            // Update the user in the users list cache
            queryClient.setQueryData(['users'], (oldData: any) => {
                if (!oldData?.data) return oldData;
                return {
                    ...oldData,
                    data: oldData.data.map((user: User) =>
                        user.id === updatedUser.id ? updatedUser : user
                    ),
                };
            });
        },
    });
};

export const useResetPassword = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: ResetPasswordRequest) => {
            const { data: response } = await httpClient.post<ApiResponse<User>>(
                `${API_ENDPOINTS.CRM.USERS}/${data.id}/reset-password`,
                {
                    password: data.password,
                }
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
};

export const useToggleUserStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (userId: string) => {
            const { data } = await httpClient.post<ApiResponse<User>>(
                `${API_ENDPOINTS.CRM.USERS}/${userId}/toggle-status`
            );
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
};

export const useReactivateUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (userId: string) => {
            const { data } = await httpClient.post<ApiResponse<User>>(
                `${API_ENDPOINTS.CRM.USERS}/${userId}/reactivate`
            );
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
};

/** Deactivates the employee (the API keeps their history; it no longer hard-deletes). */
export const useDeleteUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (userId: string) => {
            await httpClient.delete(`${API_ENDPOINTS.CRM.USERS}/${userId}`);
            return userId;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
};
